import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { WSClient, WSStatus } from '../lib/ws'

type ThreadEvent =
  | { type: 'message:new'; [k: string]: any }
  | { type: 'message:read'; [k: string]: any }
  | { type: 'typing'; [k: string]: any }
  | { type: string; [k: string]: any }

export function useThreadWS(threadId: number) {
  const clientRef = useRef<WSClient | null>(null)
  const idRef = useRef<number>(threadId)
  if (!clientRef.current || idRef.current !== threadId) {
    idRef.current = threadId
    clientRef.current = new WSClient({ path: `/ws/threads/${threadId}/`, authAsQuery: true })
  }
  const client = clientRef.current as WSClient

  const [status, setStatus] = useState<WSStatus>(client.getStatus())
  const [events, setEvents] = useState<ThreadEvent[]>([])

  const pushEvent = (evt: ThreadEvent) => setEvents((arr) => [evt, ...arr].slice(0, 200))

  const sendTyping = useCallback((isTyping = true) => {
    try { client.send({ action: 'typing', isTyping }) } catch {}
  }, [client])

  useEffect(() => {
    const offStatus = client.on('status', (s) => setStatus(s as WSStatus))
    const offMsg = client.on('message:new', (evt) => pushEvent(evt as ThreadEvent))
    const offRead = client.on('message:read', (evt) => pushEvent(evt as ThreadEvent))
    const offTyping = client.on('typing', (evt) => pushEvent(evt as ThreadEvent))
    const offFatal = client.on('fatal', () => {})
    client.connect()
    const vis = () => { if (document.visibilityState === 'hidden') client.close() }
    document.addEventListener('visibilitychange', vis)
    return () => {
      offStatus(); offMsg(); offRead(); offTyping(); offFatal()
      document.removeEventListener('visibilitychange', vis)
      try { client.send({ action: 'typing', isTyping: false }) } catch {}
      client.close()
    }
  }, [client])

  const send = useMemo(() => (msg: any) => client.send(msg), [client])
  return { status, events, sendTyping, send }
}

